/**
 * @fileoverview Two-factor verification form.
 * @description Reads the pending challenge left by LoginForm, renders the code entry + countdown,
 * and submits the code to complete sign-in. The code field lives in React Hook Form state (bridged
 * to the custom OtpInput molecule via Controller, since it's a `value`/`onChange: (value: string)`
 * component rather than a plain `<input>`) validated by twoFactorFormSchema, though the "all digits
 * entered" submit gate is still a direct length check rather than the schema's own validity, to
 * keep that gate exactly as immediate/synchronous as it was before this form used a schema. The
 * identity backend hands back two independent absolute timestamps — expiresAt (the code's total
 * validity, ~10 minutes out) and resendAvailableAt (a short ~30s cooldown before another code can
 * be requested) — so this form runs two separate useCountdown timers instead of sharing one
 * between them. The same form also serves the account-activation code (a challenge whose purpose is
 * ACTIVATION — opened by accepting an invitation, or by signing in to a registered but not yet
 * activated account): useChallengeCodeMutations picks the matching verify/resend endpoints and the
 * header/submit copy switches to activation wording. The action row and expiry footer are split
 * into small components so the main component stays under the repo's function-size limits.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { type FormEvent } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import lockIcon from '@/assets/svg/login/icon-lock.svg'
import { OTP_CODE_LENGTH } from '@/features/auth/constants/two-factor.constants'
import { useChallengeCodeMutations } from '@/features/auth/hooks/useChallengeCodeMutations'
import {
  twoFactorFormSchema,
  type TwoFactorFormValues,
} from '@/features/auth/schemas/two-factor-form.schema'
import Button from '@/shared/components/atoms/Button'
import LinkButton from '@/shared/components/atoms/LinkButton'
import OtpInput from '@/shared/components/molecules/OtpInput'
import { ERROR_CODES, type ErrorCode } from '@/shared/constants/error-codes.constants'
import { ROUTES } from '@/shared/constants/routes.constants'
import { useCountdown } from '@/shared/hooks/useCountdown'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type { TwoFactorChallengePurpose } from '@/shared/lib/auth/auth.types'
import type { ApiError } from '@/shared/types/http.types'
import type { TFunction } from 'i18next'
import type { Control } from 'react-hook-form'

// The errors that end this flow (appCodes 1006-1008 on verify, 1012-1014 on resend; 1046, 1049
// and 1051 for activation): the challenge can't be verified or resent any more, so the only way
// forward is signing in again — which, for a not-yet-activated account, opens a fresh activation
// code.
const SIGN_IN_AGAIN_ERROR_CODES: ReadonlySet<ErrorCode> = new Set([
  ERROR_CODES.MFA_MAX_ATTEMPTS_EXCEEDED,
  ERROR_CODES.MFA_MAX_RESENDS_EXCEEDED,
  ERROR_CODES.MFA_CHALLENGE_INVALID,
  ERROR_CODES.AUTH_FLOW_EXPIRED,
])

/**
 * @description Formats whole seconds as "m:ss" for the countdown/resend-button label.
 * @param {number} totalSeconds - The remaining seconds.
 * @returns {string} The formatted "m:ss" string.
 */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * @description Builds the Resend button's label: a live countdown while on cooldown, "Resending…"
 * mid-request, or a plain call to action once available.
 * @param {TFunction} t - The translate function, since this plain helper can't call hooks itself.
 * @param {number} remainingSeconds - Seconds left on the resend cooldown.
 * @param {boolean} isPending - Whether a resend request is in flight.
 * @returns {string} The label to show on the Resend button.
 */
function getResendLabel(t: TFunction, remainingSeconds: number, isPending: boolean): string {
  if (isPending) {
    return t('twoFactor.resendPending')
  }

  return remainingSeconds > 0
    ? t('twoFactor.resendWithCountdown', { countdown: formatCountdown(remainingSeconds) })
    : t('twoFactor.resendIdle')
}

/**
 * @description Picks the submit button's idle label for the challenge's purpose.
 * @param {TFunction} t - The translate function.
 * @param {TwoFactorChallengePurpose | undefined} purpose - The pending challenge's purpose.
 * @returns {string} "Activate Account" for an activation code, "Verify & Sign In" otherwise.
 */
function getSubmitLabel(t: TFunction, purpose: TwoFactorChallengePurpose | undefined): string {
  return purpose === 'ACTIVATION' ? t('twoFactor.activation.submitIdle') : t('twoFactor.submitIdle')
}

interface ActionsProps {
  submitLabel: string
  isVerifying: boolean
  isCodeComplete: boolean
  onBack: () => void
  canResend: boolean
  resendLabel: string
  onResend: () => void
}

/**
 * @description Renders the submit button plus the Back/Resend row beneath it.
 * @param {ActionsProps} props - The current pending/complete/cooldown state and handlers.
 * @param {string} props.submitLabel - The submit button's idle label.
 * @param {boolean} props.isVerifying - Whether the verify request is in flight.
 * @param {boolean} props.isCodeComplete - Whether every OTP digit has been entered.
 * @param {() => void} props.onBack - Clears the challenge and returns to the login form.
 * @param {boolean} props.canResend - Whether the resend cooldown has reached zero.
 * @param {string} props.resendLabel - The Resend button's current label.
 * @param {() => void} props.onResend - Requests a fresh code.
 */
function Actions({
  submitLabel,
  isVerifying,
  isCodeComplete,
  onBack,
  canResend,
  resendLabel,
  onResend,
}: ActionsProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-3">
      <Button
        type="submit"
        isLoading={isVerifying}
        disabled={!isCodeComplete}
        data-testid="two-factor-submit-button"
      >
        {isVerifying ? t('twoFactor.submitPending') : submitLabel}
      </Button>
      <div className="flex gap-3">
        <div className="flex-1">
          <Button
            variant="secondary"
            type="button"
            onClick={onBack}
            data-testid="two-factor-back-button"
          >
            {t('twoFactor.back')}
          </Button>
        </div>
        <div className="flex-1">
          <Button
            variant="secondary-accent"
            type="button"
            disabled={!canResend}
            onClick={onResend}
            data-testid="two-factor-resend-button"
          >
            {resendLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface ErrorMessageProps {
  error: ApiError
  onSignIn: () => void
}

/**
 * @description Renders the error under the code, followed by a "Sign in" link when the error ends
 * the login flow (see SIGN_IN_AGAIN_ERROR_CODES).
 * @param {ErrorMessageProps} props - The error to show and the sign-in handler.
 * @param {ApiError} props.error - The verify or resend error.
 * @param {() => void} props.onSignIn - Clears the challenge and returns to the login form.
 */
function ErrorMessage({ error, onSignIn }: ErrorMessageProps) {
  const { t } = useTranslation()

  return (
    <p role="alert" className="text-[14px] text-red-600" data-testid="two-factor-error-message">
      {error.message}
      {SIGN_IN_AGAIN_ERROR_CODES.has(error.code) && (
        <>
          {' '}
          <LinkButton size="sm" onClick={onSignIn} data-testid="two-factor-sign-in-link">
            {t('twoFactor.signIn')}
          </LinkButton>
        </>
      )}
    </p>
  )
}

interface ExpiryFooterProps {
  maskedEmail: string
  remainingSeconds: number
}

/**
 * @description Renders the masked-email reminder, the spam-folder hint, and a live "code expires
 * in"/"has expired" line.
 * @param {ExpiryFooterProps} props - The challenge's masked email and its expiry countdown.
 * @param {string} props.maskedEmail - The masked email address the code was sent to.
 * @param {number} props.remainingSeconds - Seconds left until the code expires.
 */
function ExpiryFooter({ maskedEmail, remainingSeconds }: ExpiryFooterProps) {
  const { t } = useTranslation()

  return (
    <p className="mt-4 text-center text-[13px] text-slate" data-testid="two-factor-expiry-footer">
      {t('twoFactor.sentToPrefix')}{' '}
      <span className="font-medium" data-testid="two-factor-masked-email">
        {maskedEmail}
      </span>
      . {t('twoFactor.spamHint')}
      <br />
      {remainingSeconds > 0 ? (
        <>
          {t('twoFactor.expiresInPrefix')}{' '}
          <span className="font-semibold" data-testid="two-factor-expiry-countdown">
            {formatCountdown(remainingSeconds)}
          </span>
          .
        </>
      ) : (
        t('twoFactor.expired')
      )}
    </p>
  )
}

/**
 * @description Renders the icon/title/description header shown above the code entry form.
 * @param {{ purpose: TwoFactorChallengePurpose | undefined }} props - The challenge's purpose, which
 * picks login or activation wording.
 */
function IntroHeader({ purpose }: { purpose: TwoFactorChallengePurpose | undefined }) {
  const { t } = useTranslation()
  const copyKey = purpose === 'ACTIVATION' ? 'twoFactor.activation' : 'twoFactor'

  return (
    <>
      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/15 bg-linear-to-br from-blue-50 to-blue-100">
        <img src={lockIcon} alt="" className="h-5 w-5" />
      </div>

      <h1
        data-testid="two-factor-title"
        className="mt-5 text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]"
      >
        {t(`${copyKey}.title`)}
      </h1>
      <p
        className="mt-1.5 text-[15px] text-slate sm:text-base"
        data-testid="two-factor-description"
      >
        {t(`${copyKey}.description`, { length: OTP_CODE_LENGTH })}
      </p>
    </>
  )
}

interface CodeInputProps {
  control: Control<TwoFactorFormValues>
  disabled: boolean
  onChangeValue: () => void
}

/**
 * @description Renders the OTP entry bridged into React Hook Form via Controller.
 * @param {CodeInputProps} props - The form control and change/disabled state.
 * @param {Control<TwoFactorFormValues>} props.control - The form's React Hook Form control.
 * @param {boolean} props.disabled - Whether verification is in flight.
 * @param {() => void} props.onChangeValue - Called on every keystroke, to reset any prior error.
 */
function CodeInput({ control, disabled, onChangeValue }: CodeInputProps) {
  const { t } = useTranslation()

  return (
    <Controller
      control={control}
      name="code"
      render={({ field }) => (
        <OtpInput
          length={OTP_CODE_LENGTH}
          value={field.value}
          onChange={(value) => {
            field.onChange(value)
            onChangeValue()
          }}
          disabled={disabled}
          aria-label={t('twoFactor.otpAriaLabel')}
          data-testid="two-factor-otp-input"
        />
      )}
    />
  )
}

/**
 * @component
 */
function TwoFactorForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { challenge, clearChallenge } = useTwoFactorChallenge()
  const { verify: verifyMutation, resend: resendMutation } = useChallengeCodeMutations(
    challenge?.purpose,
  )
  const remainingSeconds = useCountdown(challenge?.expiresAt ?? null)
  const resendRemainingSeconds = useCountdown(challenge?.resendAvailableAt ?? null)
  const { control, handleSubmit, setValue } = useForm<TwoFactorFormValues>({
    resolver: zodResolver(twoFactorFormSchema),
    defaultValues: { code: '' },
  })
  const code = useWatch({ control, name: 'code' })

  if (!challenge) {
    return null
  }

  /**
   * @description Submits the entered code, clearing it if the API rejects it (wrong code or
   * expired) so the user retypes fresh digits rather than editing a known-bad one. A "temporarily
   * unavailable" answer (503 / 1010, 1053) consumed nothing, so the code is kept for a retry.
   * @param {TwoFactorFormValues} values - The entered code.
   */
  const onValid = (values: TwoFactorFormValues) => {
    verifyMutation.mutate(
      { challengeId: challenge.challengeId, otp: values.code },
      {
        onError: (error) => {
          if (error.code !== ERROR_CODES.SERVICE_UNAVAILABLE) {
            setValue('code', '')
          }
        },
      },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid. Wrapped so the DOM
   * onSubmit handler returns void rather than the Promise React Hook Form's handleSubmit produces.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  /**
   * @description Clears the pending challenge and returns to the login form.
   */
  const handleBack = () => {
    clearChallenge()
    void navigate(ROUTES.LOGIN)
  }

  /**
   * @description Clears the error shown under the code, whichever request it came from — on every
   * keystroke and on Resend, so a stale message never sits beside a fresh attempt. An in-flight
   * resend isn't reset, since that would drop its onSuccess and leave the typed code in place.
   */
  const clearError = () => {
    verifyMutation.reset()
    if (resendMutation.isError) {
      resendMutation.reset()
    }
  }

  /**
   * @description Requests a fresh code and clears whatever was already typed, along with any error
   * from the previous attempt.
   */
  const handleResend = () => {
    clearError()
    resendMutation.mutate(
      { challengeId: challenge.challengeId },
      { onSuccess: () => setValue('code', '') },
    )
  }

  const error = verifyMutation.error ?? resendMutation.error

  return (
    <div>
      <IntroHeader purpose={challenge.purpose} />

      <form
        className="mt-6 flex flex-col gap-6"
        onSubmit={onSubmit}
        noValidate
        data-testid="two-factor-form"
      >
        <CodeInput
          control={control}
          disabled={verifyMutation.isPending}
          onChangeValue={clearError}
        />

        {error && <ErrorMessage error={error} onSignIn={handleBack} />}

        <Actions
          submitLabel={getSubmitLabel(t, challenge.purpose)}
          isVerifying={verifyMutation.isPending}
          isCodeComplete={code.length >= OTP_CODE_LENGTH}
          onBack={handleBack}
          canResend={resendRemainingSeconds <= 0 && !resendMutation.isPending}
          resendLabel={getResendLabel(t, resendRemainingSeconds, resendMutation.isPending)}
          onResend={handleResend}
        />
      </form>

      <ExpiryFooter maskedEmail={challenge.destinationMasked} remainingSeconds={remainingSeconds} />
    </div>
  )
}

export default TwoFactorForm
